/**
 * Sanitization for Sentry - ensures no health, journal, or PII is sent.
 * Use for extra/tags/context passed to captureException.
 */

/** Keys that must never be sent to Sentry (health, journal, PII) */
const BLOCKED_KEYS = new Set([
  // Health & wellbeing
  "pain",
  "pain_score",
  "painScore",
  "sleep",
  "sleep_hours",
  "sleepHours",
  "check_in",
  "checkIn",
  "checkin",
  "sentiment",
  "wellbeing",
  "heart_rate",
  "heartRate",
  "steps",
  "workout",
  "health",
  // Journal & content
  "journal",
  "journal_text",
  "journalText",
  "comment",
  "reply",
  "message",
  "response",
  "ai_response",
  "aiResponse",
  // Training plan content
  "plan_content",
  "planContent",
  "schedule",
  "custom_schedule",
  "customSchedule",
  "description",
  "weekly_schedule",
  "weeklySchedule",
  // PII
  "email",
  "name",
  "profile",
  "user_metadata",
  "userMetadata",
]);

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const CLASSIFICATION_RE = /^[A-Za-z][A-Za-z0-9_.:-]{0,127}$/;
const ISO_TIMESTAMP_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/;

/**
 * An error code in the shapes Paceback forwards: a word-like code (PGRST116, invalid_credentials)
 * or a 5-character SQLSTATE such as 42501 or 22P02, which CLASSIFICATION_RE refuses because it
 * starts with a digit.
 */
const ERROR_CODE_RE = /^(?:[A-Za-z][A-Za-z0-9_.:-]{0,63}|[0-9A-Z]{5})$/;

/**
 * Stack frames are parsed from `error.stack`, and in V8 that string begins with the whole
 * message. Every line of the message after the first is parsed like a frame, so text on it
 * became a frame's `function` or `filename` ("Knee pain 7", "/10 after the long run"). A frame
 * keeps its location only when it looks like a script, and its function only when it looks like
 * an identifier; anything else is not a frame and is dropped.
 */
const FRAME_FUNCTION_RE = /^(?:new |async )?[\w$.<>[\]]+(?: \[as [\w$]+\])?$/;
const FRAME_FILENAME_RE =
  /^(?:(?:[a-z][a-z0-9+.-]*:\/\/|\/)[^\s]*\.(?:m?js|cjs|jsx?|tsx?)|<anonymous>|\[native code\]|native)$/i;

const ID_KEYS = new Set(["userId", "planId", "clientId"]);
const CLASSIFICATION_KEYS = new Set(["component", "action", "operation", "errorType", "errorCode"]);
const NUMBER_KEYS = new Set(["lineno", "colno"]);

function isBlockedKey(key: string): boolean {
  const lower = key.toLowerCase();
  return BLOCKED_KEYS.has(key) || BLOCKED_KEYS.has(lower);
}

function sanitizeSafeValue(key: string, value: unknown): unknown {
  if (ID_KEYS.has(key)) {
    return typeof value === "string" && UUID_RE.test(value) ? value : undefined;
  }
  if (key === "errorCode") {
    return typeof value === "string" && ERROR_CODE_RE.test(value) ? value : undefined;
  }
  if (CLASSIFICATION_KEYS.has(key)) {
    return typeof value === "string" && CLASSIFICATION_RE.test(value) ? value : undefined;
  }
  if (NUMBER_KEYS.has(key)) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
  }
  if (key === "timestamp") {
    return typeof value === "string" && ISO_TIMESTAMP_RE.test(value) ? value : undefined;
  }
  if (key === "filename") {
    if (typeof value !== "string") return undefined;
    const withoutSecrets = value.split("?")[0].split("#")[0];
    return withoutSecrets.slice(-512);
  }
  return undefined;
}

/**
 * Sanitize an object for Sentry - only allow safe technical keys.
 * Removes health, journal, PII, and any unknown keys that could contain sensitive data.
 */
export function sanitizeForSentry<T extends Record<string, unknown>>(obj: T | undefined | null): Partial<T> {
  if (!obj || typeof obj !== "object") return {};

  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (isBlockedKey(key)) continue;
    const safeValue = sanitizeSafeValue(key, value);
    if (safeValue !== undefined) out[key] = safeValue;
  }
  return out as Partial<T>;
}

/**
 * Sanitize extra/context for captureException - strict allowlist.
 */
export function sanitizeSentryExtra(extra: Record<string, unknown> | undefined): Record<string, unknown> {
  return sanitizeForSentry(extra ?? {}) as Record<string, unknown>;
}

type SentryEventLike = Record<string, unknown> & {
  breadcrumbs?: unknown[];
  contexts?: Record<string, unknown>;
  exception?: { values?: Array<Record<string, unknown>> };
  extra?: Record<string, unknown>;
  request?: unknown;
  tags?: Record<string, unknown>;
  user?: Record<string, unknown>;
};

/** A script location, without its query or fragment, or undefined when it is not one. */
function frameLocation(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const location = value.split("?")[0].split("#")[0];
  return location.length <= 1024 && FRAME_FILENAME_RE.test(location) ? location : undefined;
}

/** A sanitized frame, or null when the "frame" was parsed from message text. */
function sanitizeFrame(frame: Record<string, unknown>): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  const hasLocation = typeof frame.filename === "string" || typeof frame.abs_path === "string";
  for (const key of ["filename", "abs_path"] as const) {
    const location = frameLocation(frame[key]);
    if (location) out[key] = location;
  }
  if (hasLocation && out.filename === undefined && out.abs_path === undefined) return null;
  for (const key of ["function", "module", "package"] as const) {
    const value = frame[key];
    if (typeof value === "string" && value.length <= 512 && FRAME_FUNCTION_RE.test(value)) out[key] = value;
  }
  for (const key of ["lineno", "colno"] as const) {
    const value = frame[key];
    if (typeof value === "number" && Number.isFinite(value) && value >= 0) out[key] = value;
  }
  if (typeof frame.in_app === "boolean") out.in_app = frame.in_app;
  return out;
}

function sanitizeException(exception: SentryEventLike["exception"]): SentryEventLike["exception"] {
  const values = exception?.values;
  if (!Array.isArray(values)) return undefined;
  return {
    values: values.map((value) => {
      const type = typeof value.type === "string" && CLASSIFICATION_RE.test(value.type)
        ? value.type
        : "Error";
      const stacktrace = value.stacktrace as { frames?: Array<Record<string, unknown>> } | undefined;
      const mechanism = value.mechanism as Record<string, unknown> | undefined;
      const safeValue: Record<string, unknown> = {
        type,
        value: `${type} (details redacted)`,
      };
      if (Array.isArray(stacktrace?.frames)) {
        safeValue.stacktrace = {
          frames: stacktrace.frames
            .map(sanitizeFrame)
            .filter((frame): frame is Record<string, unknown> => frame !== null),
        };
      }
      if (mechanism) {
        safeValue.mechanism = {
          ...(typeof mechanism.type === "string" && CLASSIFICATION_RE.test(mechanism.type)
            ? { type: mechanism.type }
            : {}),
          ...(typeof mechanism.handled === "boolean" ? { handled: mechanism.handled } : {}),
          ...(typeof mechanism.synthetic === "boolean" ? { synthetic: mechanism.synthetic } : {}),
        };
      }
      return safeValue;
    }),
  };
}

function sanitizeTags(tags: Record<string, unknown> | undefined): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!tags) return out;
  for (const key of ["component", "action", "operation", "expected", "errorType", "errorCode"] as const) {
    const value = tags[key];
    if (typeof value === "boolean" || (typeof value === "number" && Number.isFinite(value))) {
      out[key] = value;
    } else if (typeof value === "string" && CLASSIFICATION_RE.test(value)) {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Last-mile crash-event sanitizer. Raw exception/log text, breadcrumbs, URLs,
 * request data, arbitrary contexts, person fields, and fingerprints are removed.
 * Error type, source frames, coarse tags, and pseudonymous UUIDs remain useful.
 */
export function sanitizeSentryEvent<T extends object>(event: T): T {
  const mutable = event as SentryEventLike;
  mutable.breadcrumbs = [];
  delete mutable.request;
  delete mutable.fingerprint;
  delete mutable.logentry;
  delete mutable.message;
  delete mutable.spans;
  delete mutable.transaction;

  mutable.exception = sanitizeException(mutable.exception);
  mutable.extra = sanitizeSentryExtra(mutable.extra);
  mutable.tags = sanitizeTags(mutable.tags);

  const userId = mutable.user?.id;
  mutable.user = typeof userId === "string" && UUID_RE.test(userId) ? { id: userId } : undefined;

  const react = mutable.contexts?.react as Record<string, unknown> | undefined;
  const componentStack = react?.componentStack;
  mutable.contexts = typeof componentStack === "string"
    ? { react: { componentStack: componentStack.slice(0, 12000) } }
    : {};

  return event;
}
